import React, { useState } from "react"

import { Building2, Loader2, Plus, RotateCcw, Trash2, UserPlus, Users } from "lucide-react"
import { toast } from "sonner"

import { TableEmptyRow } from "@/components/shared/EmptyState"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { StatusPill } from "@/components/ui/status-pill"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { STAFF_EMAIL_DOMAIN, isStaffEmail, useAuth } from "@/contexts/AuthContext"
import {
  AccessEntry,
  HOUSE_OPERATOR,
  OperatorEntry,
  StaffRole,
  useAccessList,
  useAddOperator,
  useAddPartner,
  useGrantAccess,
  useOperatorList,
  useRemovePartner,
  useResetPartner,
  useRevokeAccess,
  useSetAccessRole,
  useSetOperatorCrmBase,
} from "@/hooks/useAccessList"
import { formatDateTimeShort } from "@/lib/dateFormats"

const Panel: React.FC<{
  title: React.ReactNode
  description?: React.ReactNode
  aside?: React.ReactNode
  children: React.ReactNode
}> = ({ title, description, aside, children }) => (
  <section className="overflow-hidden rounded-lg border border-palette-jungleGreen200 bg-white shadow-sm">
    <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 border-b border-palette-jungleGreen200 px-5 py-4">
      <div className="min-w-0 flex-1">
        <h3 className="text-base font-semibold text-palette-jungleGreen900">{title}</h3>
        {description && <p className="mt-1 text-sm text-palette-jungleGreen600">{description}</p>}
      </div>
      {aside}
    </div>
    {children}
  </section>
)

const PanelAddRow: React.FC<{
  onSubmit: (e: React.FormEvent) => void
  children: React.ReactNode
}> = ({ onSubmit, children }) => (
  <form
    onSubmit={onSubmit}
    className="flex flex-wrap items-end gap-3 border-b border-palette-jungleGreen200 bg-palette-jungleGreen000 px-5 py-4 last:border-b-0"
  >
    {children}
  </form>
)

const AddBox: React.FC<{
  onSubmit: (e: React.FormEvent) => void
  children: React.ReactNode
}> = ({ onSubmit, children }) => (
  <form
    onSubmit={onSubmit}
    className="flex flex-wrap items-end gap-3 rounded-lg border border-dashed border-palette-jungleGreen300 px-5 py-4"
  >
    {children}
  </form>
)

const RemoveButton: React.FC<{
  email: string
  disabledReason?: string
  onClick: () => void
}> = ({ email, disabledReason, onClick }) => (
  <Button
    variant="ghost"
    size="sm"
    aria-label={`Remove ${email}`}
    disabled={!!disabledReason}
    title={disabledReason ?? `Remove ${email}`}
    onClick={onClick}
    className="text-palette-jungleGreen600 hover:text-red-600"
  >
    <Trash2 size={14} />
  </Button>
)

const TeamPanel: React.FC<{
  entries: AccessEntry[]
  currentEmail: string
  onRemove: (entry: AccessEntry) => void
}> = ({ entries, currentEmail, onRemove }) => {
  const grant = useGrantAccess()
  const setRole = useSetAccessRole()
  const [email, setEmail] = useState("")
  const [role, setRole_] = useState<StaffRole>("operator")
  const [note, setNote] = useState("")

  const admins = entries.filter((e) => e.role === "admin")
  // Removing the last admin can only be undone in the Supabase dashboard.
  const isLastAdmin = (target: string) => admins.length === 1 && admins[0].email === target

  const onAdd = (e: React.FormEvent) => {
    e.preventDefault()
    grant.mutate(
      { email, role, note, createdBy: currentEmail },
      {
        onSuccess: () => {
          setEmail("")
          setNote("")
          setRole_("operator")
        },
      }
    )
  }

  return (
    <div className="space-y-4">
      <AddBox onSubmit={onAdd}>
        <div className="min-w-[220px] flex-1">
          <Label htmlFor="team-email">New person</Label>
          <Input
            id="team-email"
            type="email"
            required
            placeholder={`name@${STAFF_EMAIL_DOMAIN}`}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 bg-white"
          />
        </div>
        <div className="w-36">
          <Label htmlFor="team-role">Role</Label>
          <Select value={role} onValueChange={(v) => setRole_(v as StaffRole)}>
            <SelectTrigger id="team-role" className="mt-1 bg-white">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="operator">Operator</SelectItem>
              <SelectItem value="admin">Admin</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="min-w-[160px] flex-1">
          <Label htmlFor="team-note">Note (optional)</Label>
          <Input
            id="team-note"
            placeholder="Central Ops"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="mt-1 bg-white"
          />
        </div>
        <Button type="submit" variant="outline" disabled={grant.isPending} className="gap-2">
          {grant.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
          Add person
        </Button>
      </AddBox>

      <Panel
        title="HIVED"
        description="People who sign in with their HIVED Google account. Admins can also manage this page; otherwise the two roles are the same."
      >
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead className="w-40">Role</TableHead>
              <TableHead>Note</TableHead>
              <TableHead className="w-16" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {entries.length === 0 ? (
              <TableEmptyRow colSpan={4}>No one has access yet.</TableEmptyRow>
            ) : (
              entries.map((entry) => {
                const lastAdmin = isLastAdmin(entry.email)
                return (
                  <TableRow key={entry.email}>
                    <TableCell className="font-medium">
                      {entry.email}
                      {entry.email === currentEmail && (
                        <span className="ml-2 text-xs text-palette-jungleGreen400">you</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={entry.role}
                        disabled={lastAdmin || setRole.isPending}
                        onValueChange={(v) =>
                          setRole.mutate({ email: entry.email, role: v as StaffRole })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="operator">Operator</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-sm text-palette-jungleGreen600">
                      {entry.note ?? "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <RemoveButton
                        email={entry.email}
                        disabledReason={lastAdmin ? "Cannot remove the only admin" : undefined}
                        onClick={() => onRemove(entry)}
                      />
                    </TableCell>
                  </TableRow>
                )
              })
            )}
          </TableBody>
        </Table>
      </Panel>
    </div>
  )
}

/** A login sees routes whose operator is this name; its driver picker lists this CRM base. */
const CompanyPanel: React.FC<{
  company: OperatorEntry
  logins: AccessEntry[]
  currentEmail: string
  onRemove: (entry: AccessEntry) => void
  onReset: (email: string) => void
}> = ({ company, logins, currentEmail, onRemove, onReset }) => {
  const addPartner = useAddPartner()
  const setCrmBase = useSetOperatorCrmBase()
  const [email, setEmail] = useState("")
  const [note, setNote] = useState("")
  const fieldId = `company-${company.name}`

  const onAdd = (e: React.FormEvent) => {
    e.preventDefault()
    // Staff belong on the other tab and sign in with Google.
    if (isStaffEmail(email)) {
      toast.error(`A @${STAFF_EMAIL_DOMAIN} address belongs on the HIVED tab`)
      return
    }
    addPartner.mutate(
      { email, operator: company.name, note, createdBy: currentEmail },
      {
        onSuccess: () => {
          setEmail("")
          setNote("")
        },
      }
    )
  }

  return (
    <Panel
      title={company.name}
      description={
        logins.length === 0
          ? "No logins yet"
          : `${logins.length} ${logins.length === 1 ? "login" : "logins"}`
      }
      aside={
        <div className="w-60">
          <Label htmlFor={`${fieldId}-crm`}>Driver CRM base</Label>
          <Input
            id={`${fieldId}-crm`}
            defaultValue={company.driver_crm_base ?? ""}
            placeholder="e.g. DSP129-Temper"
            className="mt-1 h-9"
            onKeyDown={(e) => {
              if (e.key === "Enter") e.currentTarget.blur()
            }}
            onBlur={(e) => {
              const next = e.target.value.trim()
              if (next !== (company.driver_crm_base ?? "")) {
                setCrmBase.mutate({ name: company.name, driverCrmBase: next })
              }
            }}
          />
          {!company.driver_crm_base && (
            <p className="mt-1 text-xs text-amber-700">No drivers to pick until this is set</p>
          )}
        </div>
      }
    >
      <PanelAddRow onSubmit={onAdd}>
        <div className="min-w-[220px] flex-1">
          <Label htmlFor={`${fieldId}-email`}>Email</Label>
          <Input
            id={`${fieldId}-email`}
            type="email"
            required
            placeholder="name@company.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-1 bg-white"
          />
        </div>
        <div className="min-w-[160px] flex-1">
          <Label htmlFor={`${fieldId}-note`}>Note (optional)</Label>
          <Input
            id={`${fieldId}-note`}
            placeholder="Night dispatcher"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="mt-1 bg-white"
          />
        </div>
        <Button type="submit" disabled={addPartner.isPending} className="gap-2">
          {addPartner.isPending ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <UserPlus size={14} />
          )}
          Add login
        </Button>
      </PanelAddRow>

      {logins.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead className="w-56">Sign-up</TableHead>
              <TableHead>Note</TableHead>
              <TableHead className="w-24" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {logins.map((entry) => (
              <TableRow key={entry.email}>
                <TableCell className="font-medium">{entry.email}</TableCell>
                <TableCell>
                  {/* Nothing proves who signed up, so show when it happened. */}
                  {entry.signed_up_at ? (
                    <span className="flex items-center gap-2">
                      <StatusPill tone="success" label="Signed up" />
                      <span className="text-xs tabular-nums text-palette-jungleGreen500">
                        {formatDateTimeShort(new Date(entry.signed_up_at))}
                      </span>
                    </span>
                  ) : (
                    <StatusPill tone="neutral" label="Not signed up yet" />
                  )}
                </TableCell>
                <TableCell className="text-sm text-palette-jungleGreen600">
                  {entry.note ?? "—"}
                </TableCell>
                <TableCell className="whitespace-nowrap text-right">
                  {entry.signed_up_at && (
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={`Reset sign-up for ${entry.email}`}
                      title="Reset sign-up, so they can sign up again"
                      onClick={() => onReset(entry.email)}
                      className="text-palette-jungleGreen600"
                    >
                      <RotateCcw size={14} />
                    </Button>
                  )}
                  <RemoveButton email={entry.email} onClick={() => onRemove(entry)} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

    </Panel>
  )
}

const AddCompanyRow: React.FC<{ currentEmail: string }> = ({ currentEmail }) => {
  const addOperator = useAddOperator()
  const [name, setName] = useState("")
  const [crmBase, setCrmBase] = useState("")

  const onAdd = (e: React.FormEvent) => {
    e.preventDefault()
    addOperator.mutate(
      { name, driverCrmBase: crmBase, createdBy: currentEmail },
      {
        onSuccess: () => {
          setName("")
          setCrmBase("")
        },
      }
    )
  }

  return (
    <AddBox onSubmit={onAdd}>
      <div className="min-w-[200px] flex-1">
        <Label htmlFor="company-name">New company</Label>
        <Input
          id="company-name"
          required
          placeholder="As it appears on their routes"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="mt-1 bg-white"
        />
      </div>
      <div className="min-w-[200px] flex-1">
        <Label htmlFor="company-crm-base">Driver CRM base (optional)</Label>
        <Input
          id="company-crm-base"
          placeholder="e.g. DSP129-Temper"
          value={crmBase}
          onChange={(e) => setCrmBase(e.target.value)}
          className="mt-1 bg-white"
        />
      </div>
      <Button type="submit" variant="outline" disabled={addOperator.isPending} className="gap-2">
        {addOperator.isPending ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />}
        Add company
      </Button>
    </AddBox>
  )
}

const TabCount: React.FC<{ count: number }> = ({ count }) => (
  <span className="rounded-full bg-palette-jungleGreen200 px-1.5 text-xs tabular-nums text-palette-jungleGreen700">
    {count}
  </span>
)

const AccessContent: React.FC = () => {
  const { user } = useAuth()
  const { data: entries = [], isLoading, error } = useAccessList()
  const { data: operators = [] } = useOperatorList()
  const revoke = useRevokeAccess()
  const removePartner = useRemovePartner()
  const resetPartner = useResetPartner()

  const [tab, setTab] = useState<"team" | "partners">("team")
  const [pendingRevoke, setPendingRevoke] = useState<AccessEntry | null>(null)
  const [pendingReset, setPendingReset] = useState<string | null>(null)

  const currentEmail = user?.email?.toLowerCase() ?? ""
  const team = entries.filter((entry) => entry.role !== "partner")
  const partners = entries.filter((entry) => entry.role === "partner")
  // HIVED itself is never a partner's scope; see HOUSE_OPERATOR.
  const companies = operators.filter((operator) => operator.name !== HOUSE_OPERATOR)

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="mx-auto max-w-4xl space-y-5">
        <div>
          <h2 className="text-lg font-semibold text-palette-jungleGreen900">Manage Access</h2>
          <p className="mt-1 text-sm text-palette-jungleGreen600">
            Who can use collections: HIVED&rsquo;s own team, and the companies that run routes for
            us.
          </p>
        </div>

        {isLoading ? (
          <div className="rounded-lg border border-palette-jungleGreen200 bg-white p-6 text-sm text-palette-jungleGreen600">
            Loading…
          </div>
        ) : error ? (
          <div className="rounded-lg border border-palette-jungleGreen200 bg-white p-6 text-sm text-red-600">
            {(error as Error).message}
          </div>
        ) : (
          <Tabs value={tab} onValueChange={(next) => setTab(next as typeof tab)}>
            <TabsList className="bg-palette-jungleGreen100">
              <TabsTrigger value="team" className="gap-2">
                <Users className="h-4 w-4" />
                HIVED
                <TabCount count={team.length} />
              </TabsTrigger>
              <TabsTrigger value="partners" className="gap-2">
                <Building2 className="h-4 w-4" />
                Partners
                <TabCount count={partners.length} />
              </TabsTrigger>
            </TabsList>

            <TabsContent value="team" className="mt-4">
              <TeamPanel entries={team} currentEmail={currentEmail} onRemove={setPendingRevoke} />
            </TabsContent>

            <TabsContent value="partners" className="mt-4 space-y-4">
              <p className="text-sm text-palette-jungleGreen600">
                A partner login sees only its own company&rsquo;s routes and can only assign
                drivers. Add an email when its owner is ready to sign up: whoever signs up with it
                first gets the login.
              </p>

              <AddCompanyRow currentEmail={currentEmail} />

              {companies.map((company) => (
                <CompanyPanel
                  key={company.name}
                  company={company}
                  logins={partners.filter((entry) => entry.operator_scope === company.name)}
                  currentEmail={currentEmail}
                  onRemove={setPendingRevoke}
                  onReset={setPendingReset}
                />
              ))}
            </TabsContent>
          </Tabs>
        )}
      </div>

      <AlertDialog open={pendingRevoke !== null} onOpenChange={() => setPendingRevoke(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove access?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingRevoke?.role === "partner"
                ? `${pendingRevoke.email} will lose access to collections, and their login will be deleted.`
                : `${pendingRevoke?.email} will lose access to collections.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (pendingRevoke?.role === "partner") removePartner.mutate(pendingRevoke.email)
                else if (pendingRevoke) revoke.mutate(pendingRevoke.email)
                setPendingRevoke(null)
              }}
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={pendingReset !== null} onOpenChange={() => setPendingReset(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset sign-up?</AlertDialogTitle>
            <AlertDialogDescription>
              {pendingReset}&rsquo;s login and password are deleted, and the address can sign up
              again. Use this when they have forgotten their password, or when a sign-up
              wasn&rsquo;t theirs.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (pendingReset) resetPartner.mutate(pendingReset)
                setPendingReset(null)
              }}
            >
              Reset sign-up
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

export default AccessContent
